/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Global Command Palette (Cmd + K) Component (Task 10.1.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useEffect, useState } from 'react';
import { Command } from 'cmdk';
import { useUiStore } from '../../stores/useUiStore';
import { useTheme } from '../../context/ThemeProvider';
import { useI18n } from '../../stores/useI18nStore';
import {
  Search,
  ArrowRight,
} from 'lucide-react';
import { SophiaIcon, type SophiaDefaultIconName } from '@sofia/assets';

export interface CommandPaletteProps {
  onNavigate?: (path: string) => void;
  isOpen?: boolean;
}

interface CommandItemData {
  id: string;
  title: string;
  subtitle?: string;
  category: 'action' | 'navigation' | 'recent';
  sophiaIcon: SophiaDefaultIconName;
  icon?: React.ComponentType<{ className?: string }>;
  keywords?: string[];
  badge?: string;
  onSelect: () => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ onNavigate, isOpen: isOpenProp }) => {
  const {
    commandPaletteOpen,
    setCommandPaletteOpen,
    closeCommandPalette,
    openModal,
  } = useUiStore();
  const isOpen = isOpenProp !== undefined ? isOpenProp : commandPaletteOpen;
  const { themeMode, setThemeMode } = useTheme();
  const { t } = useI18n();
  const [searchQuery, setSearchQuery] = useState('');

  // 1. Global Keyboard Listener (Cmd+K / Ctrl+K & Custom Event)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(!commandPaletteOpen);
      } else if (e.key === 'Escape' && commandPaletteOpen) {
        e.preventDefault();
        closeCommandPalette();
      }
    };

    const handleOpenCustomEvent = () => {
      setCommandPaletteOpen(true);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('sophia:open-command-palette', handleOpenCustomEvent);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('sophia:open-command-palette', handleOpenCustomEvent);
    };
  }, [commandPaletteOpen, setCommandPaletteOpen, closeCommandPalette]);

  // Reset search when opening/closing
  useEffect(() => {
    if (!commandPaletteOpen) {
      setSearchQuery('');
    }
  }, [commandPaletteOpen]);

  const handleNavigate = (path: string) => {
    closeCommandPalette();
    if (onNavigate) {
      onNavigate(path);
    }
  };

  const handleToggleTheme = () => {
    closeCommandPalette();
    const nextMode = themeMode === 'dark' ? 'light' : 'dark';
    setThemeMode(nextMode);
  };

  const handleCreateWorkspace = () => {
    closeCommandPalette();
    openModal('create_workspace');
  };

  // 2. Hierarchical Command Data
  const quickActions: CommandItemData[] = [
    {
      id: 'action-new-article',
      title: t('command.actionNewArticleTitle'),
      subtitle: t('command.actionNewArticleDesc'),
      category: 'action',
      sophiaIcon: 'artificial-intelligence',
      keywords: ['writer', 'article', 'document', 'new', 'create'],
      badge: t('command.badgeAction'),
      onSelect: () => handleNavigate('/workspace/writer?action=new'),
    },
    {
      id: 'action-new-chat',
      title: t('command.actionNewChatTitle'),
      subtitle: t('command.actionNewChatDesc'),
      category: 'action',
      sophiaIcon: 'chat-bot',
      keywords: ['chat', 'conversation', 'assistant', 'talk'],
      badge: t('command.badgeAction'),
      onSelect: () => handleNavigate('/workspace/chat?action=new'),
    },
    {
      id: 'action-generate-image',
      title: t('command.actionGenerateImageTitle'),
      subtitle: t('command.actionGenerateImageDesc'),
      category: 'action',
      sophiaIcon: 'vision',
      keywords: ['image', 'flux', 'art', 'photo', 'dalle'],
      badge: t('command.badgeAction'),
      onSelect: () => handleNavigate('/workspace/media/generate?model=flux-pro'),
    },
    {
      id: 'action-new-translation',
      title: t('command.actionNewTranslationTitle'),
      subtitle: t('command.actionNewTranslationDesc'),
      category: 'action',
      sophiaIcon: 'nano-tech',
      keywords: ['cat', 'translation', 'tmx', 'translate', 'workbench'],
      badge: t('command.badgeAction'),
      onSelect: () => handleNavigate('/workspace/translation/workbench?action=new'),
    },
    {
      id: 'action-tts-voice',
      title: t('command.actionTtsVoiceTitle'),
      subtitle: t('command.actionTtsVoiceDesc'),
      category: 'action',
      sophiaIcon: 'voice-recognition',
      keywords: ['voice', 'tts', 'speech', 'audio'],
      badge: t('command.badgeAction'),
      onSelect: () => handleNavigate('/workspace/voice/tts?action=new'),
    },
    {
      id: 'action-topup-wallet',
      title: t('command.actionTopupWalletTitle'),
      subtitle: t('command.actionTopupWalletDesc'),
      category: 'action',
      sophiaIcon: 'battery',
      keywords: ['credits', 'wallet', 'topup', 'billing'],
      badge: t('command.badgeAction'),
      onSelect: () => handleNavigate('/workspace/billing?action=topup'),
    },
    {
      id: 'action-create-workspace',
      title: t('command.actionCreateWorkspaceTitle'),
      subtitle: t('command.actionCreateWorkspaceDesc'),
      category: 'action',
      sophiaIcon: 'structure-server',
      keywords: ['workspace', 'create workspace', 'team'],
      badge: t('command.badgeAction'),
      onSelect: handleCreateWorkspace,
    },
    {
      id: 'action-toggle-theme',
      title: t('command.actionSwitchThemeTitle', { theme: themeMode === 'dark' ? 'Light' : 'Dark' }),
      subtitle: t('command.actionSwitchThemeDesc'),
      category: 'action',
      sophiaIcon: 'hologram',
      keywords: ['theme', 'dark', 'light', 'mode'],
      badge: 'Toggle',
      onSelect: handleToggleTheme,
    },
  ];

  const navigationItems: CommandItemData[] = [
    {
      id: 'nav-dashboard',
      title: t('command.navDashboardTitle') || 'Dashboard Overview',
      subtitle: t('command.navDashboardDesc') || 'Platform overview, credit metrics & fast studio shortcuts',
      category: 'navigation',
      sophiaIcon: 'structure-server',
      keywords: ['dashboard', 'overview', 'home', 'stats', 'credits', 'workspace'],
      onSelect: () => handleNavigate('/workspace/dashboard'),
    },
    {
      id: 'nav-documents',
      title: t('command.navDocumentsTitle'),
      subtitle: t('command.navDocumentsDesc'),
      category: 'navigation',
      sophiaIcon: 'brainstorm',
      keywords: ['documents', 'files', 'storage'],
      onSelect: () => handleNavigate('/workspace/documents'),
    },
    {
      id: 'nav-wizard',
      title: t('command.navWizardTitle'),
      subtitle: t('command.navWizardDesc'),
      category: 'navigation',
      sophiaIcon: 'algorithm',
      keywords: ['wizard', 'seo', 'longform', 'article'],
      onSelect: () => handleNavigate('/workspace/wizard'),
    },
    {
      id: 'nav-assistants',
      title: t('command.navAssistantsTitle'),
      subtitle: t('command.navAssistantsDesc'),
      category: 'navigation',
      sophiaIcon: 'solution',
      keywords: ['assistants', 'personas', 'bots'],
      onSelect: () => handleNavigate('/workspace/assistants'),
    },
    {
      id: 'nav-rag',
      title: t('command.navKnowledgeTitle'),
      subtitle: t('command.navKnowledgeDesc'),
      category: 'navigation',
      sophiaIcon: 'big-data',
      keywords: ['rag', 'vectorize', 'knowledge'],
      onSelect: () => handleNavigate('/workspace/rag'),
    },
    {
      id: 'nav-video',
      title: t('command.navVideoStudioTitle'),
      subtitle: t('command.navVideoStudioDesc'),
      category: 'navigation',
      sophiaIcon: 'camera-360',
      keywords: ['video', 'luma', 'kling'],
      onSelect: () => handleNavigate('/workspace/media/video'),
    },
    {
      id: 'nav-voice-mixer',
      title: t('command.actionTtsVoiceTitle'),
      subtitle: t('command.actionTtsVoiceDesc'),
      category: 'navigation',
      sophiaIcon: 'voice-recognition',
      keywords: ['mixer', 'studio', 'audio', 'sound'],
      onSelect: () => handleNavigate('/workspace/voice/mixer'),
    },
    {
      id: 'nav-tm',
      title: t('command.actionNewTranslationTitle'),
      subtitle: t('command.actionNewTranslationDesc'),
      category: 'navigation',
      sophiaIcon: 'binary-code',
      keywords: ['tm', 'translation memory', 'tmx'],
      onSelect: () => handleNavigate('/workspace/translation/tm'),
    },
    {
      id: 'nav-developer-keys',
      title: t('nav.sections.developer'),
      subtitle: t('command.actionNewArticleDesc'),
      category: 'navigation',
      sophiaIcon: 'finger-scanner',
      keywords: ['api', 'developer', 'keys', 'token', 'docs'],
      onSelect: () => handleNavigate('/workspace/developer/keys'),
    },
    {
      id: 'nav-settings',
      title: t('command.navSettingsTitle'),
      subtitle: t('command.navSettingsDesc'),
      category: 'navigation',
      sophiaIcon: 'smart-door',
      keywords: ['settings', 'members', 'rbac'],
      onSelect: () => handleNavigate('/workspace/settings'),
    },
  ];

  const recentItems: CommandItemData[] = [
    {
      id: 'recent-doc-1',
      title: 'Q4 Enterprise AI Strategy Report',
      subtitle: 'Edited 10 mins ago in AI Writer',
      category: 'recent',
      sophiaIcon: 'strategy',
      keywords: ['report', 'strategy', 'q4'],
      badge: 'Doc',
      onSelect: () => handleNavigate('/workspace/writer?id=doc_recent_01'),
    },
    {
      id: 'recent-chat-1',
      title: 'B2B Tech Customer Persona Analysis',
      subtitle: 'AI Chat conversation with Claude 3.5 Sonnet',
      category: 'recent',
      sophiaIcon: 'chat-bot',
      keywords: ['persona', 'b2b', 'chat'],
      badge: 'Chat',
      onSelect: () => handleNavigate('/workspace/chat?id=chat_recent_01'),
    },
    {
      id: 'recent-image-1',
      title: 'Clean Energy Startup Flux Banner',
      subtitle: '2048x2048 High Resolution Visual',
      category: 'recent',
      sophiaIcon: 'vision',
      keywords: ['banner', 'clean energy', 'flux'],
      badge: 'Image',
      onSelect: () => handleNavigate('/workspace/media/gallery?id=img_recent_01'),
    },
    {
      id: 'recent-translation-1',
      title: 'EN-VI Tech Whitepaper Translation',
      subtitle: 'CAT Studio - 85% Progress',
      category: 'recent',
      sophiaIcon: 'nano-tech',
      keywords: ['whitepaper', 'translation'],
      badge: 'CAT',
      onSelect: () => handleNavigate('/workspace/translation/workbench?id=trans_recent_01'),
    },
  ];

  if (!isOpen) {
    return null;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command Palette"
      className="fixed inset-0 z-50 flex items-start justify-center pt-16 sm:pt-24 px-4 bg-background/60 backdrop-blur-md animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeCommandPalette();
        }
      }}
    >
      <div className="w-full max-w-2xl overflow-hidden rounded-2xl border border-surface-border bg-surface shadow-2xl animate-scale-in">
        <Command
          filter={(value, search) => {
            if (value.toLowerCase().includes(search.toLowerCase())) return 1;
            return 0;
          }}
          className="w-full flex flex-col"
        >
          {/* Top Search Input Strip */}
          <div className="flex items-center gap-3 px-4 py-3.5 border-b border-surface-border">
            <Search className="w-5 h-5 text-foreground-muted flex-shrink-0" />
            <Command.Input
              value={searchQuery}
              onValueChange={setSearchQuery}
              placeholder={t('command.placeholder')}
              className="w-full bg-transparent text-sm text-foreground placeholder:text-foreground-muted focus:outline-none"
              autoFocus
            />
            <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted border border-surface-border text-[11px] font-mono text-foreground-muted">
              ESC
            </kbd>
          </div>

          {/* Scrollable Results List */}
          <Command.List className="max-h-[380px] overflow-y-auto p-2 space-y-3 focus:outline-none">
            <Command.Empty className="py-12 text-center text-xs text-foreground-muted">
              {t('command.noResults')}
            </Command.Empty>

            {/* Group 1: Quick Action Shortcuts */}
            <Command.Group
              heading={t('command.quickActions')}
              className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-foreground-muted"
            >
              {quickActions.map((item) => (
                <CommandItemRow key={item.id} item={item} />
              ))}
            </Command.Group>

            {/* Group 2: Navigation & Studios */}
            <Command.Group
              heading={t('command.navigation')}
              className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-foreground-muted"
            >
              {navigationItems.map((item) => (
                <CommandItemRow key={item.id} item={item} />
              ))}
            </Command.Group>

            {/* Group 3: Recent Documents & Assets */}
            <Command.Group
              heading={t('command.recent')}
              className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-foreground-muted"
            >
              {recentItems.map((item) => (
                <CommandItemRow key={item.id} item={item} />
              ))}
            </Command.Group>
          </Command.List>

          {/* Sticky Footer: Keyboard shortcuts indicator */}
          <div className="flex items-center justify-between px-4 py-2 border-t border-surface-border bg-muted/40 text-[11px] text-foreground-muted">
            <div className="flex items-center gap-4">
              <span className="inline-flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded bg-surface border border-surface-border text-[10px] font-mono">
                  ↑
                </kbd>
                <kbd className="px-1.5 py-0.5 rounded bg-surface border border-surface-border text-[10px] font-mono">
                  ↓
                </kbd>
                <span>{t('command.navigate')}</span>
              </span>
              <span className="inline-flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded bg-surface border border-surface-border text-[10px] font-mono">
                  ↵
                </kbd>
                <span>{t('command.select')}</span>
              </span>
            </div>

            <div className="flex items-center gap-1 text-[10px] text-foreground-muted font-mono">
              <SophiaIcon name="algorithm" size={13} className="text-primary shrink-0" />
              <span>Sophia Command Engine</span>
            </div>
          </div>
        </Command>
      </div>
    </div>
  );
};

interface CommandItemRowProps {
  item: CommandItemData;
}

const CommandItemRow: React.FC<CommandItemRowProps> = ({ item }) => {
  const Icon = item.icon;
  const searchableValue = `${item.title} ${item.subtitle ?? ''} ${item.keywords?.join(' ') ?? ''}`;

  return (
    <Command.Item
      value={searchableValue}
      onSelect={item.onSelect}
      className="flex items-center justify-between px-3 py-2.5 rounded-xl text-xs cursor-pointer transition-colors text-foreground data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary group"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center text-foreground-muted group-data-[selected=true]:bg-primary group-data-[selected=true]:text-primary-fg transition-colors flex-shrink-0">
          {item.sophiaIcon ? (
            <SophiaIcon name={item.sophiaIcon} size={15} className="shrink-0" />
          ) : Icon ? (
            <Icon className="w-4 h-4" />
          ) : null}
        </div>
        <div className="min-w-0">
          <div className="font-medium truncate group-data-[selected=true]:font-semibold">
            {item.title}
          </div>
          {item.subtitle && (
            <div className="text-[11px] text-foreground-muted truncate">
              {item.subtitle}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0 ml-3">
        {item.badge && (
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-surface-border text-foreground-muted group-data-[selected=true]:bg-primary/20 group-data-[selected=true]:text-primary">
            {item.badge}
          </span>
        )}
        <ArrowRight className="w-3.5 h-3.5 opacity-0 group-data-[selected=true]:opacity-100 text-primary transition-opacity" />
      </div>
    </Command.Item>
  );
};
